import React, { useState, useEffect, useRef } from 'react';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'CEO, EduTech Solutions',
      company: 'EduTech Solutions',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      text: 'They treated our learning platform like a production system—secure releases, clear observability, and automation where it actually reduced manual work. Engagement climbed after launch because the product stayed fast and stable.',
      project: 'EdTech platform & reliability'
    },
    {
      name: 'Michael Chen',
      role: 'CTO, CloudScale Inc.',
      company: 'CloudScale Inc.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      text: 'Our AWS and Azure footprint finally has landing zones we can explain to finance and security. Migration waves were planned; FinOps discipline showed up in the bill. They did not disappear after go-live—operations mattered to them.',
      project: 'Cloud 3.0 migration & FinOps'
    },
    {
      name: 'Priya Sharma',
      role: 'Founder, HealthCare Connect',
      company: 'HealthCare Connect',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      text: 'Patient-facing mobile had to meet a high bar for trust. CodeRidge paired UX with hardened APIs and sensible identity patterns. Our clinicians stopped worrying about “whether the app would be up.”',
      project: 'Healthcare mobile & security'
    },
    {
      name: 'David Rodriguez',
      role: 'Director of Technology, RetailMax',
      company: 'RetailMax',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      text: 'Peak-season commerce needs more than a pretty storefront. They delivered performance work, payment resilience, and monitoring we could act on during incidents. Sales followed reliability.',
      project: 'E-commerce scale & observability'
    },
    {
      name: 'Emily Watson',
      role: 'Product Manager, FinTech Innovations',
      company: 'FinTech Innovations',
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      text: 'Regulators and auditors expect evidence. Their approach to access control, logging, and change history made reviews far less painful. We shipped features without treating compliance as a separate project.',
      project: 'FinTech controls & delivery'
    },
    {
      name: 'James Anderson',
      role: 'CEO, StartupHub',
      company: 'StartupHub',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      text: 'We outgrew “someone restarts the server.” Their managed coverage and runbooks meant our engineers slept. When we were ready for automation pilots, they already understood our stack.',
      project: 'Managed IT & automation roadmap'
    }
  ];

  // Auto-scroll carousel
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000); // Change slide every 5 seconds
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, testimonials.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000); // Resume after 10 seconds
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000);
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 10000);
  };

  return (
    <section id="testimonials" className="testimonials-section">
      <div className="testimonials-background">
        <div className="testimonials-shape testimonials-shape-1"></div>
        <div className="testimonials-shape testimonials-shape-2"></div>
      </div>
      
      <div className="container">
        <div className="section-header">
          <span className="section-label">Proof from the field</span>
          <h2 className="section-title">What partners say</h2>
          <p className="section-description">
            Cloud, security, automation, managed operations, and product delivery—feedback from leaders who measured outcomes, not slide decks.
          </p>
        </div>

        <div 
          className="testimonials-carousel-wrapper"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div 
            className="testimonials-carousel"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-slide">
                <div className="testimonial-card">
                  <div className="testimonial-header">
                    <div className="testimonial-image-wrapper">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        onError={(e) => {
                          e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(testimonial.name) + '&background=FF6B35&color=fff&size=150';
                        }}
                      />
                    </div>
                    <div className="testimonial-info">
                      <h4 className="testimonial-name">{testimonial.name}</h4>
                      <p className="testimonial-role">{testimonial.role}</p>
                      <p className="testimonial-company">{testimonial.company}</p>
                    </div>
                  </div>
                  
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#FFD700" stroke="#FFD700" strokeWidth="1"/>
                      </svg>
                    ))}
                  </div>

                  <p className="testimonial-text">"{testimonial.text}"</p>
                  
                  <div className="testimonial-project">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{testimonial.project}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button 
            className="carousel-nav carousel-prev" 
            onClick={goToPrevious}
            aria-label="Previous testimonial"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button 
            className="carousel-nav carousel-next" 
            onClick={goToNext}
            aria-label="Next testimonial"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Dots Indicator */}
          <div className="carousel-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${currentIndex === index ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;




